(() => {
  'use strict';
  const formats=Object.freeze({jpeg:{mime:'image/jpeg',extensions:['jpg','jpeg'],check:b=>b[0]===255&&b[1]===216&&b[2]===255},png:{mime:'image/png',extensions:['png'],check:b=>[137,80,78,71,13,10,26,10].every((v,i)=>b[i]===v)},webp:{mime:'image/webp',extensions:['webp'],check:b=>String.fromCharCode(...b.slice(0,4))==='RIFF'&&String.fromCharCode(...b.slice(8,12))==='WEBP'}});
  function dimensions(buffer,mime){
    const bytes=new Uint8Array(buffer),view=new DataView(buffer),word=(offset)=>String.fromCharCode(...bytes.slice(offset,offset+4));
    if(mime==='image/png')return bytes.length>=24&&word(12)==='IHDR'?[view.getUint32(16),view.getUint32(20)]:null;
    if(mime==='image/jpeg'){
      let p=2;
      while(p+4<=bytes.length){
        if(bytes[p++]!==255)return null;while(bytes[p]===255)p++;
        const marker=bytes[p++];if(marker===217||marker===218)return null;
        if(marker===1||marker>=208&&marker<=215)continue;
        if(p+2>bytes.length)return null;const length=view.getUint16(p);if(length<2||p+length>bytes.length)return null;
        if([192,193,194,195,197,198,199,201,202,203,205,206,207].includes(marker))return length>=7?[view.getUint16(p+5),view.getUint16(p+3)]:null;
        p+=length;
      }return null;
    }
    if(mime==='image/webp'){
      for(let p=12;p+8<=bytes.length;){
        const type=word(p),length=view.getUint32(p+4,true),start=p+8;if(start+length>bytes.length)return null;
        if(type==='VP8X'&&length>=10){if(bytes[start]&2)return null;return [1+bytes[start+4]+(bytes[start+5]<<8)+(bytes[start+6]<<16),1+bytes[start+7]+(bytes[start+8]<<8)+(bytes[start+9]<<16)];}
        if(type==='VP8L'&&length>=5&&bytes[start]===47){const bits=view.getUint32(start+1,true);return [(bits&16383)+1,((bits>>>14)&16383)+1];}
        if(type==='VP8 '&&length>=10&&bytes[start+3]===157&&bytes[start+4]===1&&bytes[start+5]===42)return [view.getUint16(start+6,true)&16383,view.getUint16(start+8,true)&16383];
        p=start+length+(length&1);
      }
    }return null;
  }
  async function validate(file){
    if(!file)return '';
    if(file.size>20*1024*1024)return 'Please choose an image smaller than 20 MB.';
    if(file.size<12)return 'Please choose a complete image file.';
    const extension=file.name.split('.').pop().toLowerCase(),head=new Uint8Array(await file.slice(0,16).arrayBuffer());
    const format=Object.values(formats).find(f=>f.extensions.includes(extension)&&f.check(head));
    if(!format||(file.type&&file.type!==format.mime))return 'Please use a JPG, PNG or WebP image.';
    const size=dimensions(await file.arrayBuffer(),format.mime);
    if(!size||!size[0]||!size[1])return 'Please choose a valid JPG, PNG or non-animated WebP image.';
    if(size[0]*size[1]>80*1000*1000||Math.max(...size)>24000)return 'Please use an image below 80 megapixels.';
    const url=URL.createObjectURL(file);
    try{
      await new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>image.naturalWidth&&image.naturalHeight?resolve():reject(Error('image'));image.onerror=()=>reject(Error('image'));image.src=url;});
    }catch{return 'This image could not be opened. Please try another JPG, PNG or WebP image.';}finally{URL.revokeObjectURL(url);}
    return '';
  }
  window.OrliImageUpload={accept:'.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp',validate};
})();

(() => {
  'use strict';
  const standard=window.OrliImageUpload,decoderUrl=document.currentScript?.dataset.heicDecoder;
  const heicExtension=file=>/\.(heic|heif)$/i.test(file?.name||'');
  let decoder;
  const decode=()=>decoder||(decoder=import(new URL(decoderUrl,location.href).href).catch(error=>{decoder=null;throw error;}));
  async function nativeJpeg(file){
    const url=URL.createObjectURL(file);
    try{
      const image=await new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>resolve(image);image.onerror=reject;image.src=url;});
      if(!image.naturalWidth||!image.naturalHeight||image.naturalWidth*image.naturalHeight>80000000)throw Error('dimensions');
      const scale=Math.min(1,4096/Math.max(image.naturalWidth,image.naturalHeight)),canvas=document.createElement('canvas');
      canvas.width=Math.round(image.naturalWidth*scale);canvas.height=Math.round(image.naturalHeight*scale);
      try{canvas.getContext('2d').drawImage(image,0,0,canvas.width,canvas.height);return await new Promise((resolve,reject)=>canvas.toBlob(blob=>blob?resolve(blob):reject(Error('encoding')),'image/jpeg',.95));}
      finally{canvas.width=canvas.height=1;}
    }finally{URL.revokeObjectURL(url);}
  }
  async function prepare(file){
    if(!file||!heicExtension(file))return {file,message:await standard.validate(file)};
    if(file.size>20*1024*1024)return {message:'Please choose an image smaller than 20 MB.'};
    if(file.size<24)return {message:'Please choose a complete HEIC or HEIF photo.'};
    const bytes=new Uint8Array(await file.arrayBuffer()),view=new DataView(bytes.buffer),word=p=>String.fromCharCode(...bytes.slice(p,p+4));
    const boxSize=view.getUint32(0),brands=new Set(['heic','heix','hevc','hevx','heim','heis','hevm','hevs','mif1','msf1']);
    if(word(4)!=='ftyp'||boxSize<16||boxSize>bytes.length||boxSize>1024)return {message:'Please choose a valid HEIC or HEIF photo.'};
    let supported=brands.has(word(8));for(let p=16;p+4<=boxSize;p+=4)supported=supported||brands.has(word(p));
    if(!supported)return {message:'Please choose a valid HEIC or HEIF photo.'};
    for(let p=4;p+16<=bytes.length;p++)if(bytes[p]===105&&bytes[p+1]===115&&bytes[p+2]===112&&bytes[p+3]===101&&view.getUint32(p-4)>=20){const w=view.getUint32(p+8),h=view.getUint32(p+12);if(w*h>80000000||Math.max(w,h)>24000)return {message:'Please use an image below 80 megapixels.'};}
    try{
      let blob;
      try{blob=await nativeJpeg(file);}catch{if(!decoderUrl)throw Error('decoder');const module=await decode();blob=await module.heicTo({blob:file,type:'image/jpeg',quality:.95});}
      const converted=new File([blob],file.name.replace(/\.(heic|heif)$/i,'.jpg'),{type:'image/jpeg',lastModified:file.lastModified});
      const message=await standard.validate(converted);
      return {file:message?null:converted,message,originalName:file.name,converted:true};
    }catch{return {message:'This iPhone photo could not be opened. Please try another photo, or use JPG or PNG.'};}
  }
  window.OrliImageUpload={...standard,accept:standard.accept+',.heic,.heif,image/heic,image/heif,image/heic-sequence,image/heif-sequence',prepare};
})();
